import { useEffect, useState, type FormEvent } from 'react';
import { loadLead, saveLeadLocal, persistLeadRemote, type Lead } from '@/lib/leads';
import { useConfiguratorStore } from '@/state/configuratorStore';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^\+?[\d\s().-]{7,20}$/;

const PERKS = [
  { icon: '01', title: 'Engineering', body: 'Robust architecture with product velocity' },
  { icon: '02', title: 'Experience', body: 'Interfaces with clarity, character & purpose' },
  { icon: '03', title: 'Visibility', body: 'Search infrastructure that compounds authority' },
  { icon: '04', title: 'Growth', body: 'Campaigns tied directly to commercial outcomes' },
] as const;

export function LeadGate() {
  const leadUnlocked = useConfiguratorStore(s => s.leadUnlocked);
  const setLead = useConfiguratorStore(s => s.setLead);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const existing = loadLead();
    if (existing) setLead(existing);
    setReady(true);
  }, [setLead]);

  if (leadUnlocked) return null;
  if (!ready) return null;

  const nameErr = !firstName.trim() ? 'First name is required' : '';
  const emailErr = !email.trim() ? 'Email is required' : !EMAIL_RE.test(email.trim()) ? 'Enter a valid email' : '';
  const phoneErr = !phone.trim()
    ? 'Mobile number is required'
    : !PHONE_RE.test(phone.trim())
      ? 'Enter a valid mobile number'
      : '';
  const invalid = Boolean(nameErr || emailErr || phoneErr);
  const showErrors = touched || submitting;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    setLocalErr(null);
    if (invalid) return;
    setSubmitting(true);
    const lead: Lead = {
      firstName: firstName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      createdAt: new Date().toISOString(),
    };
    saveLeadLocal(lead);
    setLead(lead);
    void persistLeadRemote(lead);
    setSubmitting(false);
  };

  const show = (err: string) => (showErrors && err ? err : null);

  return (
    <div className="lead-gate" role="dialog" aria-modal="true" aria-labelledby="lead-title">
      <div className="lead-gate__backdrop" aria-hidden>
        <span className="lead-gate__blob lead-gate__blob--a" />
        <span className="lead-gate__blob lead-gate__blob--b" />
        <span className="lead-gate__blob lead-gate__blob--c" />
        <span className="lead-gate__grid" />
      </div>

      <div className="lead-gate__panel">
        <aside className="lead-gate__marketing">
          <div className="lead-gate__brand-row">
            <a
              className="lead-gate__logo"
              href="https://urbanqode.com"
              target="_blank"
              rel="noreferrer"
              aria-label="UrbanQode.com"
            >
              <img src="/assets/UrbanQode_Logo.png" alt="UrbanQode" width={44} height={44} />
            </a>
            <div className="lead-gate__brand-text">
              <a className="lead-gate__domain" href="https://urbanqode.com" target="_blank" rel="noreferrer">
                UrbanQode.com
              </a>
              <span>Digital engineering studio</span>
            </div>
          </div>

          <div className="lead-gate__hero">
            <p className="lead-gate__eyebrow">Est. 2020 · PK — Global</p>
            <h1 id="lead-title" className="lead-gate__headline">
              Turning ideas
              <br />
              into digital
              <br />
              <em>reality</em>
            </h1>
            <p className="lead-gate__sub">
              Full-stack development, experience design, SEO and digital marketing — one connected growth
              engine. Unlock the 3D kit configurator and design teamwear in minutes.
            </p>
          </div>

          <ul className="lead-gate__perks">
            {PERKS.map(p => (
              <li key={p.icon}>
                <span className="lead-gate__perk-icon" aria-hidden>
                  {p.icon}
                </span>
                <div>
                  <strong>{p.title}</strong>
                  <span>{p.body}</span>
                </div>
              </li>
            ))}
          </ul>

          <div className="lead-gate__footer-meta">
            <p className="lead-gate__tagline">Build better. Grow faster.</p>
            <p className="lead-gate__contact">
              <a href="mailto:info@urbanqode.com">info@urbanqode.com</a>
              <span aria-hidden>·</span>
              <a href="tel:+923403943000">+92 340-3943000</a>
              <span aria-hidden>·</span>
              <a href="tel:+14244189710">+1 424-418-9710</a>
            </p>
          </div>
        </aside>

        <section className="lead-gate__form-side">
          <div className="lead-gate__form-header">
            <p className="lead-gate__form-eyebrow">
              <span className="lead-gate__pill">Kit Studio</span>
              Unlock configurator
            </p>
            <h2 className="lead-gate__form-title">Tell us who you are</h2>
            <p className="lead-gate__form-sub">
              First name, email and mobile are required to open the studio. We’ll only use this to follow up on your project.
            </p>
          </div>

          <form className="lead-gate__form" onSubmit={onSubmit} noValidate>
            <label className="lead-field">
              <span className="lead-field__label">First name</span>
              <input
                className={`lead-field__input${show(nameErr) ? ' is-invalid' : ''}`}
                type="text"
                name="firstName"
                autoComplete="given-name"
                placeholder="Alex"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                onBlur={() => setTouched(true)}
                required
              />
              {show(nameErr) && <span className="lead-field__error">{show(nameErr)}</span>}
            </label>

            <label className="lead-field">
              <span className="lead-field__label">Email</span>
              <input
                className={`lead-field__input${show(emailErr) ? ' is-invalid' : ''}`}
                type="email"
                name="email"
                autoComplete="email"
                placeholder="you@club.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onBlur={() => setTouched(true)}
                required
              />
              {show(emailErr) && <span className="lead-field__error">{show(emailErr)}</span>}
            </label>

            <label className="lead-field">
              <span className="lead-field__label">Mobile number</span>
              <input
                className={`lead-field__input${show(phoneErr) ? ' is-invalid' : ''}`}
                type="tel"
                name="phone"
                autoComplete="tel"
                inputMode="tel"
                placeholder="+1 424 418 9710"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                onBlur={() => setTouched(true)}
                required
              />
              {show(phoneErr) && <span className="lead-field__error">{show(phoneErr)}</span>}
            </label>

            {localErr && <p className="lead-gate__err">{localErr}</p>}

            <button type="submit" className="lead-gate__cta" disabled={submitting}>
              <span>{submitting ? 'Unlocking…' : 'Open the configurator'}</span>
              <span className="lead-gate__cta-arrow" aria-hidden>
                →
              </span>
            </button>

            <p className="lead-gate__legal">
              By continuing you agree we may contact you about this project.{' '}
              <a href="https://urbanqode.com/privacy" target="_blank" rel="noreferrer">
                Privacy
              </a>{' '}
              ·{' '}
              <a href="https://urbanqode.com/contact" target="_blank" rel="noreferrer">
                Talk to us
              </a>
            </p>
          </form>
        </section>
      </div>
    </div>
  );
}
